import { useState, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  Popover,
  Portal,
  Spinner,
  Tag,
  Text,
  useFilter,
} from '@chakra-ui/react'
import { LuChevronLeft, LuFilter, LuSearch } from 'react-icons/lu'
import { format, parseISO } from 'date-fns'
import type { Agenda } from '@/services/agendas'
import { EVENT_STATUS_CONFIG } from '@/utils/events'

export type CalendarFilterState = {
  search: string
  agendaId?: string
  agendaName?: string
  status?: string
  dateStart?: string
  dateEnd?: string
}

type FilterStep = 'type' | 'agenda' | 'status' | 'date'

type Props = {
  agendas: Agenda[]
  value: CalendarFilterState
  onChange: (filters: CalendarFilterState) => void
  isMobile?: boolean
}

const FILTER_TYPE_OPTIONS = [
  { key: 'agenda' as FilterStep, label: 'Agenda' },
  { key: 'status' as FilterStep, label: 'Status' },
  { key: 'date' as FilterStep, label: 'Data' },
]

const StepHeader = ({ label, onBack }: { label: string; onBack: () => void }) => (
  <Flex align="center" px={3} py={2} borderBottom="1px solid" borderColor="gray.100" gap={1}>
    <Box
      as="button"
      color="gray.400"
      cursor="pointer"
      _hover={{ color: 'gray.700' }}
      onClick={onBack}
      display="flex"
      alignItems="center"
    >
      <LuChevronLeft size={16} />
    </Box>
    <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
      {label}
    </Text>
  </Flex>
)

const Filters = ({ agendas, value, onChange, isMobile }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [step, setStep] = useState<FilterStep>('type')
  const [agendaSearch, setAgendaSearch] = useState('')
  const [pendingDateStart, setPendingDateStart] = useState(value.dateStart ?? '')
  const [pendingDateEnd, setPendingDateEnd] = useState(value.dateEnd ?? '')
  const [localSearch, setLocalSearch] = useState(value.search)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchChange = (newValue: string) => {
    setLocalSearch(newValue)
    setSearchLoading(true)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      onChange({ ...value, search: newValue })
      setSearchLoading(false)
    }, 1000)
  }

  const { startsWith } = useFilter({ sensitivity: 'base' })
  const filteredAgendas = useMemo(
    () => agendas.filter((a) => startsWith(a.name, agendaSearch)),
    [agendas, agendaSearch, startsWith],
  )

  const openPopover = () => {
    setStep('type')
    setPopoverOpen(true)
  }

  const closePopover = () => setPopoverOpen(false)

  const applyAgendaFilter = (agendaId: string, agendaName: string) => {
    onChange({ ...value, agendaId, agendaName })
    setAgendaSearch('')
    closePopover()
  }

  const applyStatusFilter = (status: string) => {
    onChange({ ...value, status })
    closePopover()
  }

  const applyDateFilter = () => {
    if (!pendingDateStart || !pendingDateEnd) return
    onChange({ ...value, dateStart: pendingDateStart, dateEnd: pendingDateEnd })
    closePopover()
  }

  const removeAgenda = () => {
    const { agendaId: _a, agendaName: _n, ...rest } = value
    onChange(rest)
  }

  const removeStatus = () => {
    const { status: _s, ...rest } = value
    onChange(rest)
  }

  const removeDate = () => {
    const { dateStart: _ds, dateEnd: _de, ...rest } = value
    onChange(rest)
  }

  const clearAll = () => onChange({ search: value.search })

  const hasFilters = !!(value.agendaId || value.status || value.dateStart)

  const formatShort = (dateStr: string) => format(parseISO(dateStr), 'dd/MM')

  const filterPopover = (
    <Popover.Root
      open={popoverOpen}
      onOpenChange={({ open }) => {
        if (!open) closePopover()
      }}
    >
      <Popover.Trigger asChild>
        <Button variant="outline" size="sm" onClick={openPopover} aria-label="Filtrar">
          <LuFilter />
          {!isMobile && 'Filtrar'}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="220px" p={0}>
            <Popover.Body p={0}>
              {step === 'type' && (
                <Flex direction="column">
                  <Text px={3} py={2} fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                    Filtrar por
                  </Text>
                  {FILTER_TYPE_OPTIONS.map((opt) => (
                    <Box
                      key={opt.key}
                      as="button"
                      textAlign="left"
                      px={3}
                      py="10px"
                      fontSize="sm"
                      color="gray.700"
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => setStep(opt.key)}
                    >
                      {opt.label}
                    </Box>
                  ))}
                </Flex>
              )}

              {step === 'agenda' && (
                <Flex direction="column">
                  <StepHeader label="Agenda" onBack={() => { setStep('type'); setAgendaSearch('') }} />
                  <Box px={3} py={2} borderBottom="1px solid" borderColor="gray.100">
                    <Input
                      size="sm"
                      variant="subtle"
                      placeholder="Buscar agenda..."
                      value={agendaSearch}
                      onChange={(e) => setAgendaSearch(e.target.value)}
                      autoFocus
                    />
                  </Box>
                  <Flex direction="column" maxH="180px" overflowY="auto">
                    {filteredAgendas.length > 0 ? filteredAgendas.map((agenda) => (
                      <Box
                        key={agenda.id}
                        as="button"
                        textAlign="left"
                        px={3}
                        py="10px"
                        fontSize="sm"
                        color="gray.700"
                        fontWeight={value.agendaId === agenda.id ? '700' : 'normal'}
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => applyAgendaFilter(agenda.id, agenda.name)}
                      >
                        {agenda.name}
                      </Box>
                    )) : (
                      <Text px={3} py={3} fontSize="sm" color="gray.400">Nenhuma agenda encontrada</Text>
                    )}
                  </Flex>
                </Flex>
              )}

              {step === 'status' && (
                <Flex direction="column">
                  <StepHeader label="Status" onBack={() => setStep('type')} />
                  {Object.entries(EVENT_STATUS_CONFIG).map(([key, cfg]) => (
                    <Box
                      key={key}
                      as="button"
                      textAlign="left"
                      px={3}
                      py="10px"
                      fontSize="sm"
                      color="gray.700"
                      fontWeight={value.status === key ? '700' : 'normal'}
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => applyStatusFilter(key)}
                    >
                      {cfg.label}
                    </Box>
                  ))}
                </Flex>
              )}

              {step === 'date' && (
                <Flex direction="column">
                  <StepHeader label="Data" onBack={() => setStep('type')} />
                  <Flex direction="column" gap={3} p={3}>
                    <Flex direction="column" gap={2}>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Início</Text>
                        <Input
                          type="date"
                          size="sm"
                          variant="subtle"
                          value={pendingDateStart}
                          onChange={(e) => setPendingDateStart(e.target.value)}
                        />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Fim</Text>
                        <Input
                          type="date"
                          size="sm"
                          variant="subtle"
                          value={pendingDateEnd}
                          min={pendingDateStart}
                          onChange={(e) => setPendingDateEnd(e.target.value)}
                        />
                      </Box>
                    </Flex>
                    <Button
                      size="sm"
                      colorPalette="primary"
                      disabled={!pendingDateStart || !pendingDateEnd}
                      onClick={applyDateFilter}
                    >
                      Aplicar
                    </Button>
                  </Flex>
                </Flex>
              )}
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )

  const searchInput = (
    <InputGroup
      maxW={isMobile ? 'full' : '400px'}
      w="full"
      startElement={<LuSearch size={15} />}
      endElement={searchLoading ? <Spinner size="xs" color="gray.400" /> : undefined}
    >
      <Input
        size="sm"
        bg="white"
        placeholder="Buscar eventos..."
        value={localSearch}
        onChange={(e) => handleSearchChange(e.target.value)}
      />
    </InputGroup>
  )

  const resolvedAgendaName = value.agendaName ?? agendas.find((a) => a.id === value.agendaId)?.name

  const tags = hasFilters && (
    <Flex align="center" gap={2} flexWrap="wrap">
      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" flexShrink={0}>
        Filtros:
      </Text>

      {value.agendaId && resolvedAgendaName && (
        <Tag.Root size="md" colorPalette="primary" variant="outline" bg="white" boxShadow="sm">
          <Tag.Label><Text as="span" fontWeight="700">Agenda:</Text> {resolvedAgendaName}</Tag.Label>
          <Tag.EndElement>
            <Tag.CloseTrigger onClick={removeAgenda} cursor="pointer" />
          </Tag.EndElement>
        </Tag.Root>
      )}

      {value.status && (
        <Tag.Root size="md" colorPalette="primary" variant="outline" bg="white" boxShadow="sm">
          <Tag.Label><Text as="span" fontWeight="700">Status:</Text> {EVENT_STATUS_CONFIG[value.status]?.label}</Tag.Label>
          <Tag.EndElement>
            <Tag.CloseTrigger onClick={removeStatus} cursor="pointer" />
          </Tag.EndElement>
        </Tag.Root>
      )}

      {value.dateStart && value.dateEnd && (
        <Tag.Root size="md" colorPalette="primary" variant="outline" bg="white" boxShadow="sm">
          <Tag.Label><Text as="span" fontWeight="700">Data:</Text> {formatShort(value.dateStart)} – {formatShort(value.dateEnd)}</Tag.Label>
          <Tag.EndElement>
            <Tag.CloseTrigger onClick={removeDate} cursor="pointer" />
          </Tag.EndElement>
        </Tag.Root>
      )}

      <Box
        as="button"
        fontSize="xs"
        color="gray.400"
        textDecoration="underline"
        onClick={clearAll}
        cursor="pointer"
        _hover={{ color: 'gray.600' }}
      >
        Limpar todos
      </Box>
    </Flex>
  )

  if (isMobile) {
    return (
      <Flex direction="column" gap={3} w="full">
        <Flex gap={3} align="center">
          {searchInput}
          {filterPopover}
        </Flex>
        {tags}
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap={3} w="full">
      <Flex justify="space-between" align="center" gap={3}>
        {searchInput}
        <Flex gap={3} flexShrink={0}>
          {filterPopover}
        </Flex>
      </Flex>
      {tags}
    </Flex>
  )
}

export default Filters
